import { useEffect, useState } from "react";
import { Phone, Mail, MapPin, Archive, RotateCcw, Pencil, Trash2, MessageSquarePlus, Sparkles, Image as ImageIcon } from "lucide-react";
import { api, fileUrl, formatTL, formatApiError } from "@/lib/api";
import { CUSTOMER_TYPES, TYPE_COLOR, SOURCES, NOTE_KINDS, scoreColor, timeAgo } from "@/lib/crmOptions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SendToPoolButton } from "@/components/pool/SendToPool";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { LinkedDocuments } from "@/components/scan/LinkedDocuments";

const BREAKDOWN = { price: "Fiyat", location: "Adres", keywords: "Kelime" };

const MatchCard = ({ m }) => (
  <div data-testid={`match-${m.portfolio.id}`} className="flex gap-3 rounded-lg border border-slate-200 p-3">
    <div className="w-20 h-16 rounded-md bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">
      {m.portfolio.image_paths?.[0] ? <img src={fileUrl(m.portfolio.image_paths[0], "sm")} loading="lazy" alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-slate-300" />}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-start justify-between gap-2">
        <div className="font-semibold text-sm truncate">{m.portfolio.title}</div>
        <span data-testid={`match-score-${m.portfolio.id}`} className={`text-white text-xs font-bold px-2 py-0.5 rounded-full ${scoreColor(m.score)}`}>%{m.score}</span>
      </div>
      <div className="text-xs text-slate-500">{m.portfolio.rooms} · {m.portfolio.area_m2} m² · {m.portfolio.district}, {m.portfolio.city}</div>
      <div className="flex items-center justify-between mt-1">
        <span className="text-sm font-bold text-emerald-700">{formatTL(m.portfolio.price)}</span>
        <span className="text-[11px] text-slate-500">{Object.entries(m.breakdown).map(([k, v]) => `${BREAKDOWN[k]} %${v}`).join(" · ")}</span>
      </div>
      {m.matched_keywords.length > 0 && <div className="text-[11px] text-emerald-700 mt-0.5">Eşleşen: {m.matched_keywords.join(", ")}</div>}
    </div>
  </div>
);

export const CustomerDetail = ({ customerId, onClose, onEdit, onChanged }) => {
  const [data, setData] = useState(null);
  const [matches, setMatches] = useState(null);
  const [note, setNote] = useState({ kind: "NOTE", text: "" });

  const load = async () => {
    const [{ data: d }, { data: m }] = await Promise.all([api.get(`/customers/${customerId}`), api.get(`/customers/${customerId}/matches`)]);
    setData(d);
    setMatches(m);
  };
  useEffect(() => { if (customerId) load(); }, [customerId]);

  const run = async (fn, msg, close) => {
    try { await fn(); toast.success(msg); onChanged(); close ? onClose() : load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addNote = (e) => {
    e.preventDefault();
    run(async () => { await api.post(`/customers/${customerId}/notes`, note); setNote({ kind: "NOTE", text: "" }); }, "Kaydedildi");
  };

  const c = data?.customer;
  return (
    <Dialog open={!!customerId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="customer-detail-modal">
        {!c ? <div className="p-10 text-center text-slate-500">Yükleniyor...</div> : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={TYPE_COLOR[c.customer_type]}>{CUSTOMER_TYPES[c.customer_type]}</Badge>
                <Badge variant="outline">{SOURCES[c.source] || "—"}</Badge>
                {c.archived && <Badge variant="outline" className="border-amber-400 text-amber-700">Arşivde</Badge>}
              </div>
              <DialogTitle className="text-2xl" style={{ fontFamily: "Outfit, sans-serif" }}>{c.full_name}</DialogTitle>
              <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                <a href={`tel:${c.phone}`} className="flex items-center gap-1 hover:text-emerald-700"><Phone className="w-3.5 h-3.5" />{c.phone}</a>
                {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1 hover:text-emerald-700"><Mail className="w-3.5 h-3.5" />{c.email}</a>}
                <span className="text-slate-400">Son temas: {timeAgo(c.last_contact_at)}</span>
              </div>
              {!c.archived && <div className="pt-1"><SendToPoolButton customer={c} /></div>}
            </DialogHeader>

            <Tabs defaultValue="matches">
              <TabsList>
                <TabsTrigger data-testid="tab-matches" value="matches"><Sparkles className="w-3.5 h-3.5 mr-1.5" />Önerilen Portföyler ({matches?.matches.length || 0})</TabsTrigger>
                <TabsTrigger data-testid="tab-notes" value="notes">Notlar & Görüşmeler ({data.notes.length})</TabsTrigger>
                <TabsTrigger data-testid="tab-documents" value="documents">Belgeler</TabsTrigger>
              </TabsList>

              <TabsContent value="matches" className="space-y-3 mt-4">
                <div className="text-sm rounded-lg bg-slate-50 p-3 space-y-1">
                  <div><b>Bütçe:</b> {c.budget_min || c.budget_max ? `${formatTL(c.budget_min)} – ${formatTL(c.budget_max)}` : "belirtilmedi"}</div>
                  <div className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /><b>Adres:</b> {[c.desired_neighborhood, c.desired_district, c.desired_city].filter(Boolean).join(", ") || "belirtilmedi"}</div>
                  <div><b>Aradığı:</b> {c.preferences || "belirtilmedi"}</div>
                </div>
                {matches?.matches.length ? matches.matches.map((m) => <MatchCard key={m.portfolio.id} m={m} />) : (
                  <div data-testid="no-matches" className="text-center text-slate-500 text-sm py-6">%{matches?.threshold} ve üzeri uyumlu yayında portföy yok. Bütçe, adres veya anahtar kelime ekleyerek eşleşmeyi iyileştirebilirsiniz.</div>
                )}
              </TabsContent>

              <TabsContent value="notes" className="space-y-3 mt-4">
                <form onSubmit={addNote} className="space-y-2 rounded-lg border border-slate-200 p-3">
                  <div className="flex gap-2">
                    <Select value={note.kind} onValueChange={(v) => setNote({ ...note, kind: v })}>
                      <SelectTrigger data-testid="note-kind-select" className="w-48"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(NOTE_KINDS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                    <span className="text-xs text-slate-500 self-center">Görüşme kayıtları müşteriyi listenin en üstüne taşır.</span>
                  </div>
                  <Textarea data-testid="note-text-input" rows={2} required value={note.text} onChange={(e) => setNote({ ...note, text: e.target.value })} placeholder="Not veya görüşme özeti..." />
                  <div className="flex justify-end"><Button data-testid="note-save-btn" size="sm" type="submit" className="bg-slate-900"><MessageSquarePlus className="w-4 h-4 mr-1.5" />Ekle</Button></div>
                </form>
                {c.notes && <div className="text-sm rounded-lg bg-amber-50 border border-amber-200 p-3"><b>Genel not:</b> {c.notes}</div>}
                {data.notes.map((n) => (
                  <div key={n.id} data-testid="customer-note-item" className="border-l-2 border-slate-300 pl-3 py-1">
                    <div className="text-xs text-slate-500"><Badge variant="outline" className="mr-2 text-[10px]">{NOTE_KINDS[n.kind]}</Badge>{n.author_name} · {new Date(n.created_at).toLocaleString("tr-TR")}</div>
                    <div className="text-sm text-slate-800 whitespace-pre-wrap mt-1">{n.text}</div>
                  </div>
                ))}
              </TabsContent>
              <TabsContent value="documents" className="mt-4"><LinkedDocuments customerId={c.id} /></TabsContent>
            </Tabs>

            <div className="flex justify-end gap-2 pt-4 border-t flex-wrap">
              <Button variant="outline" data-testid="customer-edit-btn" onClick={() => onEdit(c)}><Pencil className="w-4 h-4 mr-1.5" />Düzenle</Button>
              <Button variant="outline" data-testid="customer-archive-btn" onClick={() => run(() => api.put(`/customers/${c.id}/archive`, { archived: !c.archived }), c.archived ? "Arşivden çıkarıldı" : "Arşive taşındı", true)}>
                {c.archived ? <><RotateCcw className="w-4 h-4 mr-1.5" />Arşivden Çıkar</> : <><Archive className="w-4 h-4 mr-1.5" />Arşive Taşı</>}
              </Button>
              <Button variant="destructive" data-testid="customer-delete-btn" onClick={() => window.confirm("Müşteri ve notları silinsin mi?") && run(() => api.delete(`/customers/${c.id}`), "Silindi", true)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
